/**
 * Impersonate-Banner — sichtbar wenn ein Admin gerade einen anderen User
 * impersoniert (sessionStorage hat Original-Token gestasht).
 *
 * Sprint 11 (S11.2) · Erweitert um Operator-Admin-Mode: speichert auch die
 * Original-Rolle, damit der Banner-Button "ZURÜCK ZU ADMIN" oder
 * "ZURÜCK ZU SUPER-ADMIN" je nach Origin korrekt anzeigt.
 *
 * Klick → restore + redirect zurück zur passenden Admin-Seite.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';

// Legacy-Key (Block 11): Super-Admin → Tenant-Operator-Admin via /admin/tenants
const STASH_KEY = 'leadcraft_super_admin_token';
// Sprint 11 (S11.2): Operator-Admin → Tenant-User via /users
const STASH_KEY_ADMIN = 'leadcraft_operator_admin_token';

type StashOrigin = 'super_admin' | 'operator_admin' | null;

function readStash(): StashOrigin {
  if (typeof window === 'undefined') return null;
  if (sessionStorage.getItem(STASH_KEY) !== null) return 'super_admin';
  if (sessionStorage.getItem(STASH_KEY_ADMIN) !== null) return 'operator_admin';
  return null;
}

export function ImpersonateBanner() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [origin, setOrigin] = useState<StashOrigin>(() => readStash());

  useEffect(() => {
    function checkStash() {
      setOrigin(readStash());
    }
    window.addEventListener('storage', checkStash);
    const id = window.setInterval(checkStash, 2000);
    return () => {
      window.removeEventListener('storage', checkStash);
      window.clearInterval(id);
    };
  }, []);

  if (!origin || !user) return null;

  async function stop() {
    const key = origin === 'super_admin' ? STASH_KEY : STASH_KEY_ADMIN;
    const original = sessionStorage.getItem(key);
    if (!original) return;
    setAccessToken(original);
    sessionStorage.removeItem(key);
    await refreshMe();
    navigate(origin === 'super_admin' ? '/admin/tenants' : '/users');
  }

  const buttonLabel =
    origin === 'super_admin' ? 'ZURÜCK ZU SUPER-ADMIN' : 'ZURÜCK ZU ADMIN';

  return (
    <div className="impersonate-banner">
      <span className="impersonate-banner__text">
        IMPERSONATE · aktiv als {user.full_name} ({user.role})
      </span>
      <button type="button" onClick={stop} className="impersonate-banner__btn">
        {buttonLabel}
      </button>
    </div>
  );
}
